import React, { Component } from 'react'
import { StyleSheet, css } from 'aphrodite'
import { NotificationMessage, pages } from '../types'
import { ToastContainer, toast } from 'react-toastify'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import { Sidebar, TextInput } from 'components'
import 'react-toastify/dist/ReactToastify.css'
import AppStore from 'AppStore'
import {
  getListScrollPosition,
  isListPage,
  persistListScrollPosition,
} from 'utils/listViewState'

interface Props {
  page?: pages
  style?: any
  containerStyle?: any
}

interface State {
  showConfirmDialog?: boolean
  showAlert?: boolean
  showInputDialog?: boolean
  alertVariant?: variant
  title?: string
  message?: string
  onConfirm?: (text: string) => void
  onModalClosed?: () => void
  inputText?: string
}

type variant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'dark' | 'light'

export default class Page extends Component<Props, State> {
  state: State = {}
  scrollRestoreDeadline = 0
  scrollRestoreTimer?: number
  scrollSaveFrame?: number
  scrollTarget?: number
  restoringScroll = false

  showConfirmDialog = (
    title: string,
    message: string | undefined,
    onConfirm: () => void,
    onModalClosed?: () => void
  ) => {
    this.setState({ showConfirmDialog: true, title, message, onConfirm, onModalClosed })
  }

  showAlert = (title: string, variant?: variant, onModalClosed?: () => void) => {
    this.setState({ showAlert: true, title, alertVariant: variant ?? 'primary' }, onModalClosed)
  }

  showInputDialog = (
    title: string,
    message: string | undefined,
    variant?: variant,
    onConfirm?: (text: string) => void,
    onModalClosed?: () => void
  ) => {
    this.setState(
      { showInputDialog: true, alertVariant: variant ?? 'primary', title, message, onConfirm },
      onModalClosed
    )
  }

  onModalClosed = () => {
    const { onModalClosed } = this.state
    this.setState({ showConfirmDialog: false, showAlert: false, showInputDialog: false })
    onModalClosed && onModalClosed()
  }

  onModalConfirmed = () => {
    const { onConfirm, inputText } = this.state
    this.setState({ showConfirmDialog: false, showAlert: false, showInputDialog: false })
    onConfirm && onConfirm(inputText ?? 'Confirmed')
  }
  // * `One of: 'info', 'success', 'warning', 'error', 'default'`

  showError = (message: string) => this.showNotification({ message, type: 'error' })

  showNotification = (notification: NotificationMessage) => {
    const message = notification.message
    const type = notification.type
    toast(message, {
      type,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    })
  }

  componentDidMount() {
    if (AppStore.notification) {
      const notification = AppStore.notification
      this.sleep(100).then(() => this.showNotification(notification!))
      AppStore.clearNotification()
    }

    if (isListPage(this.props.page)) this.startScrollRestoration()
  }

  componentWillUnmount() {
    if (!isListPage(this.props.page)) return

    window.removeEventListener('scroll', this.handleWindowScroll)
    window.removeEventListener('pagehide', this.persistCurrentScroll)
    if (this.scrollRestoreTimer !== undefined) window.clearTimeout(this.scrollRestoreTimer)
    if (this.scrollSaveFrame !== undefined) window.cancelAnimationFrame(this.scrollSaveFrame)
    this.persistCurrentScroll()
  }

  startScrollRestoration = () => {
    const { page } = this.props
    if (!page) return

    this.scrollTarget = getListScrollPosition(page)
    window.addEventListener('scroll', this.handleWindowScroll, { passive: true })
    window.addEventListener('pagehide', this.persistCurrentScroll)

    if (this.scrollTarget === undefined) return

    this.restoringScroll = true
    this.scrollRestoreDeadline = Date.now() + 8000
    this.tryRestoreScroll()
  }

  tryRestoreScroll = () => {
    if (!this.restoringScroll || this.scrollTarget === undefined) return

    const documentHeight = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight ?? 0)
    const maxScroll = Math.max(0, documentHeight - window.innerHeight)
    const targetIsReachable = this.scrollTarget <= maxScroll + 1
    const listHasRenderedRows = Boolean(document.querySelector('.sq-page-content table tbody tr'))
    const restoreExpired = Date.now() >= this.scrollRestoreDeadline

    if (targetIsReachable || listHasRenderedRows || restoreExpired) {
      window.scrollTo(0, Math.min(this.scrollTarget, maxScroll))
      this.restoringScroll = false
      this.scrollRestoreTimer = undefined
      return
    }

    this.scrollRestoreTimer = window.setTimeout(this.tryRestoreScroll, 50)
  }

  handleWindowScroll = () => {
    if (this.restoringScroll || this.scrollSaveFrame !== undefined) return

    this.scrollSaveFrame = window.requestAnimationFrame(() => {
      this.scrollSaveFrame = undefined
      this.persistCurrentScroll()
    })
  }

  persistCurrentScroll = () => {
    const { page } = this.props
    if (!page) return
    persistListScrollPosition(page, window.scrollY)
  }

  sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  render() {
    const { page, style, containerStyle } = this.props
    const { showConfirmDialog, showAlert, message, title, alertVariant, showInputDialog } = this.state
    return (
      <div className={page ? 'app-shell' : undefined} style={{ ...style }}>
        {page && <Sidebar page={page} />}
        <main className={page ? 'app-shell__main' : undefined}>
          <Modal show={showConfirmDialog} onHide={this.onModalClosed}>
            <Modal.Header closeButton>
              <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            {message && <Modal.Body style={{ whiteSpace: 'pre-wrap' }}>{message}</Modal.Body>}
            <Modal.Footer>
              <Button variant="secondary" onClick={this.onModalClosed}>
                Chiudi
              </Button>
              <Button variant="primary" onClick={this.onModalConfirmed}>
                Conferma
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal show={showAlert} onHide={this.onModalClosed} style={{ marginTop: 150 }}>
            <Alert variant={alertVariant} style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {title}
            </Alert>
          </Modal>

          <Modal show={showInputDialog} onHide={this.onModalClosed} style={{ marginTop: 150 }}>
            <Modal.Header closeButton>
              <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <TextInput
              label={message ?? 'Scrivi qui'}
              required={true}
              placeholder={'????'}
              onChange={(value) => this.setState({ inputText: value })}
              className={css(s.inputText)}
            />
            <Modal.Footer>
              <Button variant="secondary" onClick={this.onModalClosed}>
                Chiudi
              </Button>
              <Button variant="primary" onClick={this.onModalConfirmed}>
                Conferma
              </Button>
            </Modal.Footer>
          </Modal>
          <div style={{ display: 'flex', flex: 1 }}>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </div>
          <div className="sq-page-content" style={containerStyle}>
            {this.props.children}
          </div>
        </main>
      </div>
    )
  }
}

const s = StyleSheet.create({
  inputText: {
    margin: 20,
  },
})
